import React, { ReactNode, useContext, useState, useCallback, useRef } from "react";
import moment from "moment-timezone";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import Icon from "@mdi/react";
import { mdiUnfoldMoreHorizontal, mdiUnfoldLessHorizontal } from "@mdi/js";

import { initializeApollo } from "src/lib/apollo-client";
import EmailStateCapture from "src/components/EmailStateCapture";
import GamePicks from "src/components/GamePicks";
import Hero from "src/components/Hero";
import heroStyles from "src/components/Hero/styles.scss";
import { Sport, SPORTS, WEB_SPORTS_MAP } from "src/constants";
import { EXTRAS_CONTENT_QUERY, FREE_EVENTS_QUERY } from "src/gql/queries";
import styles from "src/styles/free-picks.scss";
import { useLocalStorage } from "src/services/UtilService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import LandingPageModal, { LandingPageModalApi } from "src/components/LandingPageModal";
import { UpgradeButtonBehaviorContext } from "src/components/UpgradeButton";
import OffersCarousel from "src/components/OffersCarousel";
import { renderRichText } from "src/services/PrismicService";
import OfferCarouselModal from "src/components/OfferCarouselModal";
import { isPpcPage } from "src/services/NoIndexService";

const PageHero = () => {
  const ctx = useContext(SubscriptionContext);
  const [emailInStorage] = useLocalStorage("emailInLocalStorage");
  const emailCaptured = ctx.signedIn || emailInStorage;

  return (
    <>
      <div className="hero-section">
        {emailCaptured ? (
          <Hero />
        ) : (
          <div className="hero" style={{ height: 300 }}>
            <p className="headline">Sign Up for a Free BetQL Account to Unlock Best Bets</p>
            <style jsx>{heroStyles}</style>
          </div>
        )}
      </div>
      {!emailCaptured && <EmailStateCapture ctaText="UNLOCK PICKS" />}
    </>
  );
};

const SportCollapsible = ({ sport, children }: { sport: Sport; children: ReactNode }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="sport-collapsible">
      <div className="collapsible-header" onClick={() => setOpen(!open)}>
        <img className="sport-icon" style={{ height: 26, marginRight: 12 }} src={sport.icon} />
        <h3 className="collapsible-title">{sport.name}</h3>
        <Icon path={open ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal} size="24px" />
      </div>
      {open && children}
    </div>
  );
};

const FreePicksPage = ({ freeEvents, extrasContent }: FreePicksProps) => {
  const ctx = useContext(SubscriptionContext);
  const router = useRouter();
  const [emailInStorage] = useLocalStorage<boolean>("emailInLocalStorage");
  const emailCaptured = ctx.signedIn || emailInStorage;
  const modalApiRef = useRef<LandingPageModalApi | null>();

  const onFreePicksSignUpClick = useCallback(() => {
    modalApiRef.current?.open();
  }, [modalApiRef.current]);

  const sports = Object.values(SPORTS);
  const leagues = Object.values(WEB_SPORTS_MAP);

  // Only include events in next day or week
  freeEvents = freeEvents.filter((event: any) => {
    const league = leagues.find((x) => x.key === event.league.name);
    return moment(event.startDate)
      .subtract(league?.hasConfiguredSchedule ? 7 : 1, "days")
      .isBefore();
  });

  // Separate events by sport
  let eventsBySport = sports.map((sport) => ({
    sport,
    events: freeEvents.filter((event: any) => {
      const league = leagues.find((x) => x.key === event.league.name);
      return league?.sport === sport;
    }),
  }));

  const freePicksTitle =
    freeEvents.length !== 0 ? "" : `${freeEvents.length ? "" : "No "}Free Picks for ${moment().format("MMMM Do YYYY")}`;

  return (
    <div className="free-picks-page">
      <NextSeo
        title={freePicksTitle}
        openGraph={{
          url: `https://www.betql.co${router?.asPath}`,
          images: [],
        }}
        noindex={isPpcPage(router?.asPath || "")}
      />
      <PageHero />
      <h2 className="subheading">{freePicksTitle}</h2>
      <div className="sports-list">
        <UpgradeButtonBehaviorContext.Provider
          value={{
            onUpgradeClickOverride: onFreePicksSignUpClick,
            upgradeTextOverride: "Sign Up to Access",
          }}
        >
          {eventsBySport.map(
            ({ sport, events }) =>
              Boolean(events.length) && (
                <SportCollapsible sport={sport} key={sport.name}>
                  {events.map((event: any) => (
                    // event now cached so can be passed by slug
                    <GamePicks
                      key={event.slugId}
                      slug={event.slugId}
                      ssr={true}
                      accessOverride={emailCaptured || false}
                    />
                  ))}
                </SportCollapsible>
              )
          )}
        </UpgradeButtonBehaviorContext.Provider>
        <OffersCarousel />
      </div>
      <OfferCarouselModal />
      {extrasContent ? <div className="extras-content">{renderRichText(extrasContent)}</div> : null}
      <style jsx>{styles}</style>
      <LandingPageModal api={(api) => (modalApiRef.current = api)} />
    </div>
  );
};

export default FreePicksPage;

interface FreePicksProps {
  freeEvents: any[];
  extrasContent: string;
}

export const getStaticProps: GetStaticProps<FreePicksProps> = async function () {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query<{ freeEvents: any[] }>({
    query: FREE_EVENTS_QUERY,
  });
  const extrasContentResponse = await apolloClient.query<{ prismicExtras: { content: string } }, { uid: string }>({
    query: EXTRAS_CONTENT_QUERY,
    variables: {
      uid: "free-picks",
    },
  });

  return {
    props: {
      extrasContent: extrasContentResponse?.data?.prismicExtras?.content || "",
      freeEvents: data?.freeEvents || [],
    },
    revalidate: 60 * 60, // every hour
  };
};
